import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Upload image to Firebase Storage
 * @param {File} file - Image file to upload
 * @param {string} userId - User ID for organizing uploads
 * @param {string} folder - Optional folder name (e.g., 'templates', 'logos')
 * @returns {Promise<string>} - Download URL of uploaded image
 */
export async function uploadImage(file, userId, folder = 'templates') {
  if (!file) throw new Error('No file provided');
  if (!userId) throw new Error('User ID is required');

  // Create unique filename with timestamp
  const timestamp = Date.now();
  const filename = `${folder}/${userId}/${timestamp}-${file.name}`;

  // Create storage reference
  const storageRef = ref(storage, filename);

  // Upload file
  await uploadBytes(storageRef, file);

  // Get download URL
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

/**
 * Upload multiple images
 * @param {File[]} files - Array of image files
 * @param {string} userId - User ID
 * @param {string} folder - Optional folder name
 * @returns {Promise<string[]>} - Array of download URLs
 */
export async function uploadMultipleImages(files, userId, folder = 'templates') {
  const uploadPromises = files.map(file => uploadImage(file, userId, folder));
  return Promise.all(uploadPromises);
}

/**
 * Convert a PNG data URL to a Blob.
 */
function dataURLToBlob(dataURL) {
  const [header, data] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Upload a template thumbnail (rendered PNG data URL) to Firebase Storage.
 * Overwrites the previous thumbnail for the template at a stable path.
 * @param {string} dataURL - PNG data URL
 * @param {string} userId - User ID
 * @param {string} templateId - Template document ID
 * @returns {Promise<string>} - Download URL of uploaded thumbnail
 */
export async function uploadThumbnail(dataURL, userId, templateId) {
  if (!dataURL) throw new Error('No thumbnail data provided');
  if (!userId) throw new Error('User ID is required');
  if (!templateId) throw new Error('Template ID is required');

  const blob = dataURLToBlob(dataURL);
  const storageRef = ref(storage, `templates/${userId}/thumbnails/${templateId}.png`);

  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}
